import { HttpParams } from '@angular/common/http';
import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import {
  MonitoringOriginEntityAggregateEntity,
  RelatedEntityScreeningStateAggregateEntity,
  ScreeningCaseStateEntity,
  ScreeningEntityScreeningCaseStateEntity,
  ScreeningStateAggregate,
} from '@nk/data-entities-lib';
import { NullableObject, OrderBy } from '@nk/utils-lib';

export type ScreeningStatusTag = keyof Omit<
  RelatedEntityScreeningStateAggregateEntity,
  'number_of_entity_monitoring_entities' | 'screening_case_state_update_timestamp'
>;

export const SCREENING_STATUS_TAGS_TRANSLATIONS: Record<ScreeningStatusTag, string> = {
  pep_declined_flag: extractTranslation('screening.monitoring_origin_entity.tags.pep_declined_flag'),
  pep_flag: extractTranslation('screening.monitoring_origin_entity.tags.pep_flag'),
  pep_match_declined_flag: extractTranslation('screening.monitoring_origin_entity.tags.pep_match_declined_flag'),
  possible_pep_flag: extractTranslation('screening.monitoring_origin_entity.tags.possible_pep_flag'),
  possible_sanction_flag: extractTranslation('screening.monitoring_origin_entity.tags.possible_sanction_flag'),
  sanction_flag: extractTranslation('screening.monitoring_origin_entity.tags.sanction_flag'),
  sanction_match_declined_flag: extractTranslation(
    'screening.monitoring_origin_entity.tags.sanction_match_declined_flag',
  ),
  pending_screening_actions_flag: extractTranslation(
    'screening.monitoring_origin_entity.tags.pending_screening_actions_flag',
  ),
  no_active_monitoring_connections_flag: extractTranslation(
    'screening.monitoring_origin_entity.tags.no_active_monitoring_connections_flag',
  ),
};

export const SCREENING_STATUS_TAGS = Object.keys(SCREENING_STATUS_TAGS_TRANSLATIONS) as ScreeningStatusTag[];

function isScreeningStatusTag(item: string): item is ScreeningStatusTag {
  return SCREENING_STATUS_TAGS.includes(item as ScreeningStatusTag);
}

export const SCREENING_STATUS_CSS_CLASSES: Record<ScreeningStatusTag, string> = {
  pep_declined_flag: 'text-success',
  pep_flag: 'text-warning',
  pep_match_declined_flag: '',
  possible_pep_flag: 'text-warning',
  possible_sanction_flag: 'text-warning',
  sanction_flag: 'text-danger',
  sanction_match_declined_flag: '',
  pending_screening_actions_flag: 'text-warning',
  no_active_monitoring_connections_flag: 'text-warning',
};

export type ScreeningRelatedEntityTableFilterSet = Omit<
  RelatedEntityScreeningStateAggregateEntity,
  'number_of_entity_monitoring_entities' | 'screening_case_state_update_timestamp'
>;

export interface MonitoringOriginEntityTableFiltersForm {
  search_query: string;
  entity_type: string | null;
  include_history_flag: boolean;
  watchlist_uuids: string[] | null;
  pep_declined_flag: boolean | null;
  pep_flag: boolean | null;
  pep_match_declined_flag: boolean | null;
  possible_pep_flag: boolean | null;
  possible_sanction_flag: boolean | null;
  sanction_flag: boolean | null;
  sanction_match_declined_flag: boolean | null;
  pending_screening_actions_flag: boolean | null;
  no_active_monitoring_connections_flag: boolean | null;
}

export function getMonitoringOriginEntityTableFilterSet<T extends Partial<Record<ScreeningStatusTag, unknown>>>(
  data: T,
): ScreeningRelatedEntityTableFilterSet {
  const defaultFilterSet: ScreeningRelatedEntityTableFilterSet = {
    pep_declined_flag: null,
    pep_flag: null,
    pep_match_declined_flag: null,
    possible_pep_flag: null,
    possible_sanction_flag: null,
    sanction_flag: null,
    sanction_match_declined_flag: null,
    pending_screening_actions_flag: null,
    no_active_monitoring_connections_flag: null,
  };
  if (!data) {
    return defaultFilterSet;
  }
  for (const key in data) {
    const element = data[key];
    if (typeof element === 'boolean' && element === true) {
      defaultFilterSet[key as ScreeningStatusTag] = element;
    }
  }
  return defaultFilterSet;
}

export function getScreeningStatusTags<
  T extends
    | ScreeningStateAggregate
    | ScreeningCaseStateEntity
    | ScreeningEntityScreeningCaseStateEntity
    | MonitoringOriginEntityAggregateEntity,
>(data: T): ScreeningStatusTag[] {
  if (!data) {
    return [];
  }
  const tags: ScreeningStatusTag[] = [];
  for (const [key, value] of Object.entries(data)) {
    if (isScreeningStatusTag(key) && value === true) {
      tags.push(key);
    }
  }
  return tags;
}

export interface FetchMonitoringOriginEntityCompositeCollectionParams {
  screeningConfigUuid: string;
  searchQuery?: string | null;
  watchlistUuids?: string[] | null;
  companyUuid?: string | null;
  customerPersonUuid?: string | null;
  entityKey?: string | null;
  screeningEntityUuid?: string | null;
  monitoringOriginEntityAggregateFilterSet?: Partial<NullableObject<MonitoringOriginEntityAggregateEntity>> | null;
  includeHistoryFlag?: boolean | null;
  orderBy: OrderBy[];
  limit: number | null;
  offset: number | null;
}

export function getMonitoringOriginEntityCompositeHttpParams(
  params: FetchMonitoringOriginEntityCompositeCollectionParams,
): HttpParams {
  let httpParams = new HttpParams();
  if (params.searchQuery) {
    httpParams = httpParams.append('search_query', params.searchQuery);
  }
  if (params.companyUuid) {
    httpParams = httpParams.append('company_identifier', params.companyUuid);
  }
  if (params.customerPersonUuid) {
    httpParams = httpParams.append('customer_person_identifier', params.customerPersonUuid);
  }
  if (params.entityKey) {
    httpParams = httpParams.append('entity_key', params.entityKey);
  }
  if (params.screeningEntityUuid) {
    httpParams = httpParams.append('screening_entity_uuid', params.screeningEntityUuid);
  }
  if (Array.isArray(params.watchlistUuids) && params.watchlistUuids.length) {
    httpParams = httpParams.append('watchlist_uuids', params.watchlistUuids.join(','));
  }
  if (params.monitoringOriginEntityAggregateFilterSet) {
    for (const [filterKey, value] of Object.entries(params.monitoringOriginEntityAggregateFilterSet)) {
      if (isScreeningStatusTag(filterKey) && typeof value === 'boolean') {
        httpParams = httpParams.append(filterKey, String(value));
      }
    }
  }
  if (params.includeHistoryFlag) {
    httpParams = httpParams.append('include_history_flag', String(params.includeHistoryFlag));
  }
  if (params.orderBy?.length) {
    httpParams = httpParams.append('order_by', params.orderBy.join(','));
  }
  if (params.offset) {
    httpParams = httpParams.append('offset', String(params.offset));
  }
  if (params.limit) {
    httpParams = httpParams.append('limit', String(params.limit));
  }
  return httpParams;
}
